/**
 * Google Mobile Auth-style clean authentication popup & route (`/login`).
 *
 * Two factors, in the only order that works: the email code issues the WebAuthn challenge, so the
 * code comes first and the passkey second. 1. email → 2. 6-digit code → 3. passkey assertion (an
 * existing account) or passkey creation (a new one). The code is not a fallback any more; without
 * it there is nothing for the authenticator to sign.
 *
 * Rendered as a Google-style floating auth card with backdrop blur.
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useAuth } from "../auth/AuthContext";
import { verifyOtp, type OtpTicket, type OtpVerification } from "../api";
import { authDebug } from "../auth/debug";
import { diagnosePasskey, installPasskeyDebug } from "../auth/passkeyDebug";
import { TaskJinnLogo } from "../components/TaskJinnLogo";

type AuthStep = "email" | "otp" | "passkey";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });

  const [step, setStep] = useState<AuthStep>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [deviceName] = useState("Primary Device Passkey");
  const [ticket, setTicket] = useState<OtpTicket | null>(null);
  const [verification, setVerification] = useState<OtpVerification | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [diagnosis, setDiagnosis] = useState<string | null>(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const destination = search.redirect ?? "/account";

  const handleClose = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      void navigate({ to: "/" });
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // `window.tjPasskeyDebug("you@example.com")` — see ../auth/passkeyDebug.ts.
  useEffect(() => {
    installPasskeyDebug();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return;

    setNotice(null);
    auth.clearError();
    setChecking(true);
    try {
      const issued = await auth.requestOtp(cleanEmail);
      setTicket(issued.ticket);
      setPreview(issued.devOtpPreview ?? null);
      setVerification(null);
      setStep("otp");
      setNotice(`We sent a 6-digit code to ${cleanEmail}`);
    } catch {
      // auth.error is set by the provider
    } finally {
      setChecking(false);
    }
  };

  /**
   * "The browser showed the QR instead of my passkey" has no error to read, so the failure gets a
   * button: it compares what the account owns with what this browser actually holds and prints the
   * verdict on the card (and to the console). Same code path as `window.tjPasskeyDebug(email)`.
   */
  const handleDiagnose = async () => {
    setDiagnosing(true);
    setDiagnosis(null);
    try {
      const result = await diagnosePasskey(email.trim().toLowerCase());
      setDiagnosis(result.verdict);
    } catch (cause) {
      setDiagnosis(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setDiagnosing(false);
    }
  };

  /**
   * Replaces every device record with a passkey created here.
   *
   * For the orphaned case: the record the account has names a credential no authenticator holds
   * any more, so it can never be opened. The old seed is therefore already unrecoverable, and the
   * repair rotates it — which is why the confirmation says so before anything happens.
   */
  const handleRepairDevice = async () => {
    if (!verification) return;
    const confirmed = window.confirm(
      "This replaces this account's passkey records with a new passkey on this device, and starts " +
        "a new vault key.\n\nAnything encrypted under the old key cannot be opened again. Continue?",
    );
    if (!confirmed) return;
    try {
      await auth.completeSignup(verification, {
        deviceName: "This device (repaired)",
        replaceDevices: true,
      });
      await navigate({ to: destination });
    } catch (cause) {
      authDebug("login-page:repair-failed", {
        error: cause instanceof Error ? cause.message : String(cause),
      });
      // auth.error renders below
    }
  };

  const handlePasskeySignIn = async (options?: { allowHybrid?: boolean }) => {
    if (!verification) return;
    try {
      await auth.signIn(verification, options);
      await navigate({ to: destination });
    } catch (cause) {
      authDebug("login-page:sign-in-failed", {
        allowHybrid: Boolean(options?.allowHybrid),
        error: cause instanceof Error ? cause.message : String(cause),
      });
      // auth.error renders below
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket) return;
    auth.clearError();
    setChecking(true);
    try {
      const verified = await verifyOtp({
        email: email.trim().toLowerCase(),
        code: code.trim(),
        ticket,
      });
      setVerification(verified);
      const existing = verified.device_envelopes?.length ?? 0;
      authDebug("login-page:code-verified", { existingDevices: existing });
      if (existing > 0) {
        setStep("passkey");
        return;
      }
      // A brand-new account: there is nothing to assert, so create the first passkey now.
      await auth.completeSignup(verified, { deviceName });
      await navigate({ to: destination });
    } catch (cause) {
      authDebug("login-page:verify-failed", {
        error: cause instanceof Error ? cause.message : String(cause),
      });
      // auth.error renders below
    } finally {
      setChecking(false);
    }
  };

  const handleResendCode = async () => {
    setNotice(null);
    auth.clearError();
    try {
      const issued = await auth.requestOtp(email.trim().toLowerCase());
      setTicket(issued.ticket);
      setPreview(issued.devOtpPreview ?? null);
      setNotice("New verification code sent to your inbox.");
    } catch {
      // auth.error renders below
    }
  };

  const handleReset = () => {
    setStep("email");
    setTicket(null);
    setVerification(null);
    setPreview(null);
    setCode("");
    setNotice(null);
    setDiagnosis(null);
    auth.clearError();
  };

  // If already authenticated, show a clean signed-in card
  if (auth.session) {
    return (
      <div
        className="auth-backdrop"
        data-testid="login-page"
        onClick={handleBackdropClick}
      >
        <div className="auth-modal-card text-center">
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="auth-close-btn"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <TaskJinnLogo className="w-12 h-12 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-stone-900 tracking-tight">You are signed in</h1>
          <p className="mt-2 text-sm text-stone-500">
            {auth.session.email ?? "TensileAuth session"} · {auth.session.role ?? "member"}
          </p>

          <div className="mt-6 flex flex-col gap-2.5">
            <Link to="/dashboard" className="tj-btn tj-btn-primary w-full py-3 text-center justify-center font-semibold">
              Open Dashboard
            </Link>
            <Link to="/account" className="tj-btn tj-btn-secondary w-full py-3 text-center justify-center font-semibold">
              Account &amp; Devices
            </Link>
            <button
              type="button"
              className="text-xs text-rose-600 hover:text-rose-700 font-semibold mt-2 py-1 cursor-pointer"
              onClick={() => {
                auth.signOut();
                handleReset();
              }}
            >
              Sign out of this session
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="auth-backdrop"
      data-testid="login-page"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      onClick={handleBackdropClick}
    >
      <div className="auth-modal-card">
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="auth-close-btn"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Brand Header */}
        <div className="flex flex-col items-center text-center">
          <TaskJinnLogo className="w-11 h-11 mb-2.5 transition-transform hover:scale-105" />
          <h1 id="auth-modal-title" className="text-2xl font-bold text-stone-900 tracking-tight">
            {step === "email" && "Sign in to TaskJinn"}
            {step === "otp" && "Check your email"}
            {step === "passkey" && "Verify it's you"}
          </h1>
          <p className="mt-1 text-sm text-stone-500">
            {step === "email" && "Enter your email to continue"}
            {step === "otp" && "Enter the 6-digit code we just sent, then your passkey"}
            {step === "passkey" && "Code accepted — approve with your passkey"}
          </p>

          {/* Email Chip with 'Change' option */}
          {step !== "email" && (
            <div className="auth-chip">
              <span className="truncate max-w-[200px]">{email}</span>
              <button
                type="button"
                onClick={handleReset}
                className="auth-chip-change"
              >
                Change
              </button>
            </div>
          )}
        </div>

        {/* Feedback Banners */}
        <div aria-live="polite" className="mt-4 space-y-2">
          {auth.error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-medium text-red-800" role="alert">
              <p>{auth.error}</p>
              {/* A support reference beats "it didn't work": it links this attempt to a device
                  profile and a trace the operator can read. */}
              {auth.traceReference && (
                <p className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-normal text-red-700">
                  <span>
                    Reference:{" "}
                    <code
                      className="rounded border border-red-200 bg-white px-1.5 py-0.5 font-mono font-bold"
                      data-testid="trace-reference"
                    >
                      TJ-{auth.traceReference}
                    </code>
                  </span>
                  <button
                    type="button"
                    className="underline hover:no-underline cursor-pointer"
                    onClick={() => auth.downloadTrace()}
                  >
                    Download full log
                  </button>
                </p>
              )}
            </div>
          )}
          {notice && (
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-xs font-medium text-orange-900">
              {notice}
            </div>
          )}
          {preview && (
            <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex items-center justify-between">
              <span>Local development code:</span>
              <code data-testid="otp-preview" className="font-mono font-bold text-sm bg-white px-2 py-0.5 rounded border border-amber-200">
                {preview}
              </code>
            </div>
          )}
        </div>

        {/* ─── STEP 1: EMAIL INPUT ────────────────────────────────────────── */}
        {step === "email" && (
          <form className="mt-6 flex flex-col gap-4" onSubmit={handleEmailSubmit}>
            <div>
              <label htmlFor="login-email-input" className="auth-label">
                Work or personal email
              </label>
              <input
                id="login-email-input"
                type="email"
                name="email"
                autoComplete="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="auth-input"
              />
            </div>

            <button
              type="submit"
              className="tj-btn tj-btn-primary w-full py-3.5 text-base font-semibold rounded-xl shadow-sm hover:shadow transition-all justify-center mt-1"
              disabled={checking || auth.busy}
              aria-label="Send verification code"
            >
              {checking ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Checking account…
                </span>
              ) : (
                "Continue"
              )}
            </button>
          </form>
        )}

        {/* ─── STEP 2 (EXISTING): PASSKEY AUTHENTICATION ──────────────────── */}
        {step === "passkey" && (
          <div className="mt-6 flex flex-col gap-4">
            <div className="my-3 flex flex-col items-center justify-center">
              <div className="relative w-20 h-20 rounded-full bg-orange-50 border-2 border-orange-200/80 flex items-center justify-center text-3xl shadow-inner">
                <span className="animate-pulse">🛡️</span>
                <div className="absolute inset-0 rounded-full border-2 border-[var(--accent-terracotta)]/30 animate-ping" />
              </div>
              <p className="mt-3 text-xs text-stone-500 font-medium text-center">
                Fast, passwordless authentication using your hardware Passkey.
              </p>
            </div>

            <button
              type="button"
              className="tj-btn tj-btn-primary w-full py-3.5 text-base font-semibold rounded-xl shadow-sm hover:shadow transition-all justify-center"
              onClick={() => void handlePasskeySignIn()}
              disabled={auth.busy}
            >
              {auth.busy ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Authenticating…
                </span>
              ) : (
                "Sign in with Passkey"
              )}
            </button>

            <button
              type="button"
              className="text-xs text-stone-500 hover:text-[var(--accent-terracotta)] font-medium text-center cursor-pointer"
              onClick={() => void handlePasskeySignIn({ allowHybrid: true })}
              disabled={auth.busy}
            >
              Use a phone or tablet instead
            </button>

            <button
              type="button"
              className="text-[11px] text-stone-400 hover:text-stone-600 font-medium text-center cursor-pointer"
              onClick={handleDiagnose}
              disabled={diagnosing || auth.busy}
              data-testid="diagnose-passkey"
            >
              {diagnosing ? "Checking this browser's passkeys…" : "Not seeing your passkey? Check this browser"}
            </button>

            <button
              type="button"
              className="text-[11px] text-amber-700 hover:text-amber-900 font-semibold text-center cursor-pointer"
              onClick={handleRepairDevice}
              disabled={auth.busy}
              data-testid="repair-device"
            >
              Passkey on this device not recognised? Repair it
            </button>

            {diagnosis && (
              <div
                className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-medium text-amber-900"
                role="status"
                data-testid="passkey-diagnosis"
              >
                {diagnosis}
              </div>
            )}
          </div>
        )}

        {/* ─── STEP 2 (NEW): 6-DIGIT EMAIL VERIFICATION ──────────────────── */}
        {step === "otp" && (
          <form className="mt-6 flex flex-col gap-4" onSubmit={handleVerifyOtp}>
            <div>
              <label htmlFor="login-otp-input" className="auth-label">
                Verification code
              </label>
              <input
                id="login-otp-input"
                type="text"
                name="otp_code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                className="auth-input auth-input-otp"
              />
            </div>

            <button
              type="submit"
              className="tj-btn tj-btn-primary w-full py-3.5 text-base font-semibold rounded-xl shadow-sm hover:shadow transition-all justify-center"
              disabled={auth.busy || code.length < 6}
              aria-label="Verify code"
            >
              {auth.busy ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Setting up Passkey…
                </span>
              ) : (
                "Verify code"
              )}
            </button>

            <div className="flex items-center justify-between text-xs text-stone-500 mt-1">
              <button
                type="button"
                className="hover:text-[var(--accent-terracotta)] font-medium cursor-pointer"
                onClick={handleResendCode}
                disabled={auth.busy}
              >
                Resend code
              </button>
              <button
                type="button"
                className="hover:text-[var(--accent-terracotta)] font-medium cursor-pointer"
                onClick={handleReset}
              >
                Use different email
              </button>
            </div>
          </form>
        )}

        {/* Security Trust Footer */}
        <div className="mt-8 pt-5 border-t border-stone-100 flex items-center justify-center gap-2 text-xs text-stone-400 font-medium">
          <svg className="w-3.5 h-3.5 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span>End-to-end Passkey encryption · Passwords never touch servers</span>
        </div>
      </div>
    </div>
  );
}
